import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/api/admin/users');
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || '사용자 목록을 불러올 수 없습니다.');
    }
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async ({ username, password, role }, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/api/admin/users', { username, password, role });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || '사용자 생성에 실패했습니다.');
    }
  }
);

export const updateUserRole = createAsyncThunk(
  'users/updateUserRole',
  async ({ id, role }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/api/admin/users/${id}/role`, { role });
      return { id, role, message: data.message };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || '권한 변경에 실패했습니다.');
    }
  }
);

export const resetUserPassword = createAsyncThunk(
  'users/resetUserPassword',
  async ({ id, password }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/api/admin/users/${id}/password`, { password });
      return { id, message: data.message };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    }
  }
);

export const toggleUserActive = createAsyncThunk(
  'users/toggleUserActive',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/api/admin/users/${id}/active`);
      return { id, active: data.active, message: data.message };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || '상태 변경에 실패했습니다.');
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/api/admin/users/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || '사용자 삭제에 실패했습니다.');
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearUsersError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      // fetchUsers
      .addCase(fetchUsers.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      // createUser
      .addCase(createUser.fulfilled, (state, action) => {
        if (action.payload.user) {
          state.items.push(action.payload.user);
        }
      })
      // updateUserRole
      .addCase(updateUserRole.fulfilled, (state, action) => {
        const idx = state.items.findIndex((u) => u._id === action.payload.id);
        if (idx !== -1) state.items[idx].role = action.payload.role;
      })
      // toggleUserActive
      .addCase(toggleUserActive.fulfilled, (state, action) => {
        const idx = state.items.findIndex((u) => u._id === action.payload.id);
        if (idx !== -1) state.items[idx].active = action.payload.active;
      })
      // deleteUser
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.items = state.items.filter((u) => u._id !== action.payload);
      });
  },
});

export const { clearUsersError } = usersSlice.actions;
export default usersSlice.reducer;
